import React, { useState } from 'react';
import { NCZIEntry } from '../../../../Interfaces/ncziTypes';
import EHREntry from '../EHREntry/EHREntry';
import useNCZICommandProcess from '../../../../Hooks/useNCZICommandProcess';
import NCZICommandResult from '../../../NCZICommandResult/NCZICommandResult';
import LinkButton from '../../../../../../Components/Button/LinkButton';
import { Theme } from '../../../../../../Enums/Theme.enum';
import { CancelExaminationResult } from '../../../../Commands/CancelExaminationResult';
import moment from 'moment-timezone';
import RemoveButton from '../../../../../../Components/Button/RemoveButton';
import { CancelVaccinationResult } from '../../../../Commands/CancelVaccinationResult';
import ElectronicVaccinationCancellationConfirmationModal from '../../../ElectronicVaccination/ElectronicVaccinationCancellationConfirmation/ElectronicVaccinationCancellationConfirmationModal';
import { ElectronicVaccinationCancellationConfirmationValues } from '../../../ElectronicVaccination/ElectronicVaccinationCancellationConfirmation/ElectronicVaccinationCancellationConfirmation';
import ElectronicVaccinationReactionCancellationConfirmationModal from '../../../ElectronicVaccination/ElectronicVaccinationReactionCancellationConfirmation/ElectronicVaccinationReactionCancellationConfirmationModal';
import { ElectronicVaccinationReactionCancellationConfirmationValues } from '../../../ElectronicVaccination/ElectronicVaccinationReactionCancellationConfirmation/ElectronicVaccinationReactionCancellationConfirmation';
import { CancelVaccinationReaction } from '../../../../Commands/CancelVaccinationReaction';
import TakeOverModal from '../../../ElectronicExamination/TakeOver/TakeOverModal';
import ElectronicExaminationCreateModal from '../../../ElectronicExamination/Create/ElectronicExaminationCreateModal';

interface Props {
	onCancel: (needReload: boolean) => void;
	entry: NCZIEntry;
	patientIdentifier: string;
}

const AT_ZOOV =
	'CEN-EN13606-ENTRY.Zaznam_o_vysetreni-Zaznam_o_odbornom_vysetreni.v7/at0000.1';
const AT_OZ = 'CEN-EN13606-SECTION.Ockovaci_zaznam.v1/at0001.3';
const AT_OZR = 'CEN-EN13606-SECTION.Ockovaci_zaznam.v1/at0000';

const EHREntryModalContent: React.FC<Props> = props => {
	const { onCancel, entry, patientIdentifier } = props;
	const [
		isElectronicVaccinationCancellationConfirmationOpen,
		setIsElectronicVaccinationCancellationConfirmationOpen,
	] = useState<boolean>(false);
	const [
		isElectronicVaccinationReactionCancellationConfirmationOpen,
		setIsElectronicVaccinationReactionCancellationConfirmationOpen,
	] = useState<boolean>(false);
	const [isTakeOverOpen, setIsTakeOverOpen] = useState<boolean>(false);
	const [isTakeOverConfirmed, setIsTakeOverConfirmed] =
		useState<boolean>(false);

	const { processingCommandId, processCommand, isProcessing } =
		useNCZICommandProcess({
			onFinish: () => onCancel?.(true),
		});

	const handleCancelExamination = async () => {
		if (!entry.recordId) {
			return;
		}

		const stornoCommand = CancelExaminationResult({
			patientCode: patientIdentifier,
			type: 'ZoOV',
			examination: {
				previousVersion: entry.structured.previousVersion || entry.recordId,
				versionSetId: entry.structured.versionSetId || undefined,
				versionStatus: 'VER04',
				sensitivity: 3,
				createdAt: moment().format(),
				uncertaintyExpressed: false,
			},
		});

		await processCommand(stornoCommand);
	};

	const handleCancelVaccination = async () => {
		if (!entry.recordId) {
			return;
		}

		const stornoCommand = CancelVaccinationResult({
			patientCode: patientIdentifier,
			vaccination: {
				previousVersion: entry.recordId,
				versionStatus: 'VER04',
				sensitivity: 3,
				createdAt: moment().format(),
				uncertaintyExpressed: false,
				reason: '-zde důvod-',
			},
		});

		await processCommand(stornoCommand);
	};

	if (isProcessing && processingCommandId) {
		return <NCZICommandResult commandId={processingCommandId} />;
	}

	console.log({ entry });

	const handleConfirmElectronicVaccinationCancellation = async (
		values: ElectronicVaccinationCancellationConfirmationValues,
	) => {
		if (!entry.recordId) {
			return;
		}

		const stornoCommand = CancelVaccinationResult({
			patientCode: patientIdentifier,
			vaccination: {
				previousVersion: entry.recordId,
				versionStatus: 'VER04',
				sensitivity: 3,
				createdAt: moment().format(),
				uncertaintyExpressed: false,
				reason: values.reason,
			},
		});

		await processCommand(stornoCommand);
		setIsElectronicVaccinationCancellationConfirmationOpen(false);
	};

	const handleConfirmElectronicVaccinationReactionCancellation = async (
		values: ElectronicVaccinationReactionCancellationConfirmationValues,
	) => {
		if (!entry.recordId) {
			return;
		}

		const stornoCommand = CancelVaccinationReaction({
			patientCode: patientIdentifier,
			vaccinationReaction: {
				previousVersion: entry.recordId,
				versionStatus: 'VER04',
				sensitivity: 3,
				createdAt: moment().format(),
				uncertaintyExpressed: false,
				reason: values.reason,
			},
		});

		await processCommand(stornoCommand);
		setIsElectronicVaccinationReactionCancellationConfirmationOpen(false);
	};

	const handleTakeOver = () => {
		setIsTakeOverOpen(false);
		setIsTakeOverConfirmed(true);
	};

	const handleTakeOverClose = (needReload: boolean) => {
		setIsTakeOverConfirmed(false);
		if (needReload) {
			onCancel(true);
		}
	};

	const isStorned = entry.info?.version === 'VER04';

	return (
		<>
			<EHREntry item={entry} />

			{entry.recordId && entry.archetypeId === AT_ZOOV && !isStorned && (
				<>
					{entry.structured?.examination?.recommendationForExamination &&
						!entry.structured?.examination?.recommendationForExamination
							.result && (
							<>
								<LinkButton
									title={'nczi.action.takeOverVL'}
									onClick={() => setIsTakeOverOpen(true)}
									theme={Theme.BLUE}
								/>
								<br />
							</>
						)}
					<RemoveButton
						title={'nczi.action.cancelExamination'}
						onClick={() => handleCancelExamination()}
						showConfirmation={false}
					/>
				</>
			)}
			{entry.recordId && entry.archetypeId === AT_OZ && !isStorned && (
				<LinkButton
					title={'nczi.action.cancelVaccination'}
					onClick={() =>
						setIsElectronicVaccinationCancellationConfirmationOpen(true)
					}
					theme={Theme.RED}
				/>
			)}
			{entry.recordId && entry.archetypeId === AT_OZR && !isStorned && (
				<LinkButton
					title={'nczi.action.cancelVaccinationReaction'}
					onClick={() =>
						setIsElectronicVaccinationReactionCancellationConfirmationOpen(true)
					}
					theme={Theme.RED}
				/>
			)}

			<ElectronicVaccinationCancellationConfirmationModal
				isOpen={isElectronicVaccinationCancellationConfirmationOpen}
				onClose={() =>
					setIsElectronicVaccinationCancellationConfirmationOpen(false)
				}
				onSubmit={handleConfirmElectronicVaccinationCancellation}
			/>
			<ElectronicVaccinationReactionCancellationConfirmationModal
				isOpen={isElectronicVaccinationReactionCancellationConfirmationOpen}
				onClose={() =>
					setIsElectronicVaccinationReactionCancellationConfirmationOpen(false)
				}
				onSubmit={handleConfirmElectronicVaccinationReactionCancellation}
			/>
			{entry.structured?.examination?.recommendationForExamination &&
				!entry.structured?.examination?.recommendationForExamination.result && (
					<>
						<TakeOverModal
							patientIdentifier={patientIdentifier}
							recordId={
								entry.structured?.examination?.recommendationForExamination.rcId
									.code
							}
							opened={isTakeOverOpen}
							onClose={() => setIsTakeOverOpen(false)}
							onConfirm={handleTakeOver}
						/>
						<ElectronicExaminationCreateModal
							opened={isTakeOverConfirmed}
							onClose={handleTakeOverClose}
							patientIdentifier={patientIdentifier}
							takeOverExamination={entry}
						/>
					</>
				)}
		</>
	);
};

export default EHREntryModalContent;
